    import React, { useRef, useState } from 'react';
import API from '@services/pesquisapreco';
import ProdutoAPI from '@services/produto';
import ConcorrenteAPI from '@services/concorrente';
import AliquotaAPI from '@services/aliquota';
import EstadoAPI from '@services/estado';
import ImpostoAPI from '@services/imposto';
import { CrudPage } from '@components/organisms';
import { Spinner } from '@components/atoms';
import { Toast } from '@components/molecules';
import FindReplaceIcon from '@material-ui/icons/FindReplace';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import UnidadeMedidaAPI from '@services/unidademedida';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import Typography from '@material-ui/core/Typography';
import fileDownload from 'js-file-download';
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ImportDialog = ({ open, loading, onClose, onImport }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [errors, setErrors] = useState([]);
    const fileInput = useRef(null);

    const reset = () => {
        setSelectedFile(null);
        setErrors([]);
        if (fileInput.current) {
            fileInput.current.value = '';
        }
    };

    const close = () => {
        if (loading) return;
        reset();
        onClose();
    };

    const submit = async () => {
        if (!selectedFile || loading) return;
        setErrors([]);
        try {
            await onImport(selectedFile);
            reset();
        } catch (error) {
            const responseErrors = error.response && error.response.data && error.response.data.errors;
            setErrors(Array.isArray(responseErrors) && responseErrors.length > 0
                ? responseErrors
                : [{ mensagem: 'Não foi possível importar o arquivo.' }]);
        }
    };

    const selectFile = ({ target }) => {
        const file = target.files && target.files[0];
        setErrors([]);

        if (!file) {
            setSelectedFile(null);
            return;
        }

        if (!file.name.toLowerCase().endsWith('.xlsx')) {
            setSelectedFile(null);
            target.value = '';
            setErrors([{ mensagem: 'Selecione um arquivo no formato .xlsx.' }]);
            return;
        }

        setSelectedFile(file);
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={close}>
            <DialogTitle>Importar pesquisa de preço</DialogTitle>
            <DialogContent dividers>
                <InputLabel htmlFor="pesquisa-import-button" style={{ color: 'black' }}>
                    <Input
                        id="pesquisa-import-button"
                        inputRef={fileInput}
                        inputProps={{
                            accept: '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        }}
                        onChange={selectFile}
                        style={{ display: 'none' }}
                        type="file"
                    />
                    <Button color="secondary" variant="contained" component="span" disabled={loading}>
                        {selectedFile ? selectedFile.name : 'Selecionar arquivo .xlsx'}
                    </Button>
                </InputLabel>
                {errors.length > 0 && (
                    <div style={{ marginTop: 20 }}>
                        {errors.map((error, index) => (
                            <Typography key={index} color="error" variant="body2">
                                {error.linha !== null && error.linha !== undefined
                                    ? `Linha ${error.linha}: `
                                    : ''}
                                {error.mensagem}
                            </Typography>
                        ))}
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={loading}>Cancelar</Button>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={!selectedFile || loading}
                    onClick={submit}
                    startIcon={<PublishIcon />}
                >
                    Importar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default function PesquisaPrecoPage() {
    const [labelPreco, setLabelPreco] = useState('Preço');
    const [, setLabelImposto] = useState();
    const [importModal, setImportModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [forceReload, setForceReload] = useState(false);
    const [toast, setToast] = useState(null);

    const downloadTemplate = async () => {
        if (loading) return;
        try {
            setLoading(true);
            const response = await API.template();
            fileDownload(response.data, 'pesquisa_preco.xlsx');
        } catch (error) {
            setToast({ severity: 'error', message: 'Não foi possível baixar o template.' });
        } finally {
            setLoading(false);
        }
    };

    const importFile = async (file) => {
        if (loading) return;
        setLoading(true);
        try {
            const response = await API.importar(file);
            setImportModal(false);
            setToast({
                severity: 'success',
                message: `Pesquisa ${response.data.id} importada com ${response.data.itens} item(ns).`
            });
            setForceReload(true);
            setTimeout(() => setForceReload(false), 1000);
        } catch (error) {
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const extraButtons = [
        <Button
            key="download-template"
            variant="contained"
            color="default"
            style={{ marginLeft: '10px' }}
            disabled={loading}
            onClick={downloadTemplate}
            startIcon={<GetAppIcon />}
        >
            Baixar template
        </Button>,
        <Button
            key="import-excel"
            variant="contained"
            color="primary"
            style={{ marginLeft: '10px' }}
            disabled={loading}
            onClick={() => setImportModal(true)}
            startIcon={<PublishIcon />}
        >
            Importar Excel
        </Button>
    ];

    return (
        <>
            <Spinner open={loading} />
            <CrudPage
                api={API}
                title={'Preços'}
                subtitle={'Pesquisa'}
                app={'pesquisa_preco'}
                model={'pesquisapreco'}
                can_change={false}
                can_delete={false}
                forceReload={forceReload}
                extra_buttons={extraButtons}
                listColumns={
                    [
                        {
                            field: 'id',
                            headerName: 'Número da Pesquisa',
                            flex: 1
                        },
                        {
                            field: 'data',
                            headerName: 'Data',
                            flex: 1
                        },                         
                        {
                            field: 'estado',
                            headerName: 'Estado',
                            flex: 1,                          
                        },  
                        {
                            field: 'concorrente',
                            headerName: 'Concorrente',
                            flex: 1,                          
                        },                                               
                    ]
                }
                fields={[
                    {
                        type:'autocomplete',
                        name:'concorrente', 
                        label: 'Concorrente',
                        required: true,
                        api: ConcorrenteAPI,
                    },
                    {
                        type:'autocomplete',
                        name:'impostos', 
                        label: 'Impostos',
                        required: true,
                        api: ImpostoAPI,
                        multiple:true,
                        renderOption:(option, { selected }) => (
                            <React.Fragment>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {option.label}
                            </React.Fragment>
                        ),
                        disableCloseOnSelect:true,
                        onChange:(v, usecruds) => {
                            const {getValues} = usecruds();
                            const impostos = getValues('impostos');  
                            if(impostos.length){
                                let text = impostos.map((el) => el.label).join(', ');
                                setLabelImposto(text);
                                setLabelPreco(`Preço com ICMS + "${text}":`);
                            }                          
                        }
                    }, 
                    {
                        type:'autocomplete',
                        name:'estado', 
                        label: 'Estado',
                        required: true,
                        api: EstadoAPI,
                        xs:6
                    },                     
                    {
                        type:'autocomplete',
                        name:'aliquota', 
                        label: 'Alíquota ICMS',
                        required: true,
                        api: AliquotaAPI,
                        xs:6
                    },                                       
                ]}
                search={[]}
                pageIcon={<FindReplaceIcon />}
                fullScreen={true}
                showtab={true}
                inlines={[
                    {
                        name: 'itens',
                        title: 'Produtos',
                        fields:
                            [ 
                                {
                                    type:'autocomplete',
                                    name:'produto', 
                                    label: 'Produto',
                                    required: true,
                                    api: ProdutoAPI,
                                    onChange:(value, usecruds) => {
                                        const {formset, inlineIndex, setValue} = usecruds();
                                        if(value){
                                            ProdutoAPI.detail(value.id).then((r) => {
                                                setValue(`${formset}_${inlineIndex}_unidade_medida`, r.data.unidade_medida);
                                            })
                                        }  
                                    }                                    
                                },
                                {
                                    type: 'autocomplete',
                                    name: 'unidade_medida',
                                    label: 'Un.',
                                    required: true,
                                    disabled: true,
                                    api: UnidadeMedidaAPI,
                                    xs:3
                                },                             
                                {
                                    type:'money',
                                    name:'preco', 
                                    label: labelPreco,
                                    required: true,
                                    xs:9
                                },                                                                                                                                
                            ]
                    }
                ]}

            />
            <ImportDialog
                open={importModal}
                loading={loading}
                onClose={() => setImportModal(false)}
                onImport={importFile}
            />
            {toast && (
                <Toast
                    open={!!toast}
                    severity={toast.severity}
                    duration={4000}
                    handleClose={() => setToast(null)}
                >
                    {toast.message}
                </Toast>
            )}
        </>
    );
}
